import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../api';
import { offerDue } from '../reminder-state';
import { readFacts } from '../reminders';
import { feel } from '../feel';
import { currentLang, t } from '../i18n';
import { go } from '../router';
import { headlineOf, rankFirsts, type EndSummary } from '../town/end-summary';
import type { LearnEnd } from '../types';
import { aroundWord, comebackLine, firstKey, promiseLine } from '../week';

/**
 * The end's few lines (v1 ticket 37, laid out as the end over the town, variant B), at the end of a round's payoff,
 * a tending round, a moment's first lesson and a lesson in test mode:
 *
 * - One headline, the most meaningful change (`headlineOf`): a first (the highest; the first word out of the fog
 *   brings the calendar under it, and any other first follows in a short line), else the words out of the fog, else
 *   the houses climbed, else lamps lit again, else ivy cleared, else (with no miss either) an honest "nothing moved".
 * - One line: the score over scored cards only ("5 of 6 right"), then the honest promise, "By this time tomorrow in
 *   your town: …" (the town's name once it has one, v1 ticket 40), and the missed words in one line ("Worth another
 *   look: …", at most five, then "+N").
 * - At the end of a round's payoff (`round`), an old recording whose lines where the learner knows every word grew
 *   comes back in one line, with a button that opens it, its new lines marked (v1 ticket 31).
 *
 * `compact` (the walk bar of a house tended from home) keeps the headline, its firsts and the missed words only.
 *
 * It fires `end-ready` (bubbling) once its lines are on screen, so the town above can frame its chips over what is
 * left of the screen.
 *
 * It asks `POST /api/learn/end` once, after `landed` settles (the answers have landed, so the promise counts
 * them). That call marks nothing: only once the firsts it returns have actually been painted does this ack
 * them with `POST /api/learn/firsts/seen` (and the comeback with `POST /api/learn/comeback/seen`), so a reply lost before it reaches the browser (a dropped
 * connection, the app killed mid-fetch) costs nothing — the same firsts simply come back at the next end. A
 * failed ack is left alone for the same reason. A first plays the build's sound; under reduced motion it
 * fades in instead of popping. When the first call fails, the headline and score still show from `summary`.
 */
@customElement('end-note')
export class EndNote extends LitElement {
  /** Settles once the answers of the round or lesson have landed (or failed). */
  @property({ attribute: false }) landed?: Promise<unknown>;
  /** The end of a round's payoff: the only end that brings an old recording back. */
  @property({ type: Boolean }) round = false;
  /** The sitting's counts, for the headline and the score; null while they are being read. */
  @property({ attribute: false }) summary: EndSummary | null = null;
  /**
   * The walk bar's lines (a house tended from home): the headline and any firsts, and the missed words; no score and
   * no promise. Firsts are read and acked exactly as at any other end.
   */
  @property({ type: Boolean }) compact = false;
  /** The words with a scored miss this sitting, as named (Finglish first for Persian): "Worth another look: …". */
  @property({ attribute: false }) missed: readonly string[] = [];
  @state() private end: LearnEnd | null = null;
  /** The end call answered or failed: the lines can show. */
  @state() private settled = false;
  /** The first-round reminder offer (v1 ticket 38): only once its data arrived, never holding the end up. */
  @state() private offer = false;

  protected createRenderRoot() { return this; }

  protected firstUpdated() {
    void this.read();
  }

  private async read() {
    let end: LearnEnd;
    try {
      await this.landed;
      end = await api.learnEnd(this.round);
      if (!this.isConnected) return;
      this.end = end;
      this.settled = true;
      if (end.firsts.length) feel('build');
    } catch {
      // The end stands without its firsts and promise: the headline and score still show.
      this.settled = true;
      await this.ready();
      return;
    }
    // The ack waits for the render, and for the element to still be there to have shown it, so a first is
    // only ever marked shown once it was actually on screen.
    await this.ready();
    if (this.round) void this.askOffer();
    if (!this.isConnected || (!end.firsts.length && !end.comeback)) return;
    // Best effort: an un-acked first or comeback just shows again at the next end.
    if (end.firsts.length) void api.ackFirstsSeen(end.firsts.map((f) => f.kind)).catch(() => undefined);
    if (end.comeback) void api.ackComebackSeen(end.comeback).catch(() => undefined);
  }

  /** A round's end offers reminders once, when this phone can have them and none is on; a failed read shows nothing. */
  private async askOffer() {
    try {
      const s = await api.reminders();
      const f = await readFacts(s.devices);
      if (!this.isConnected || !offerDue(s, f)) return;
      this.offer = true;
      // Offered once it is painted: ignoring it never brings it back.
      void api.reminderOffer(false).catch(() => undefined);
      await this.ready();
    } catch {
      // No offer.
    }
  }

  private take = (used: boolean) => {
    this.offer = false;
    void api.reminderOffer(used).catch(() => undefined);
    if (used) go('/#reminders');
  };

  /** The lines are painted: tell the screen, which frames the town's chips over what is left of it. */
  private async ready() {
    await this.updateComplete;
    if (this.isConnected) this.dispatchEvent(new CustomEvent('end-ready', { bubbles: true }));
  }

  render() {
    if (!this.settled) return nothing;
    const e = this.end, s = this.summary;
    const firsts = rankFirsts(e?.firsts ?? []);
    const head = headlineOf(s, firsts);
    const line = e && !this.compact ? promiseLine(e.promise, t, currentLang(), e.town ?? null) : null;
    const score = s?.scored && !this.compact ? t('end.score', { right: s.right, n: s.scored }) : '';
    return html`<div class="end-note" role="status">
      ${this.renderHead(head)}
      ${firsts.slice(head?.kind === 'first' && head.first !== 'fog' ? 1 : 0).map((f) => this.renderFirst(f.kind, f.word))}
      ${score || line
        ? html`<div class="end-line small">${score ? html`<span class="end-score">${score}</span>` : nothing}${score && line ? t('common.dot') : ''}${line
            ? html`<span class="end-promise">${this.named(line)}</span>`
            : nothing}</div>`
        : nothing}
      ${this.renderMissed()}
      ${this.renderComeback()}
      ${this.offer
        ? html`<div class="end-offer small"><p>${t('reminder.offer.line')}</p>
            <div class="row"><button class="btn small primary end-offer-open" @click=${() => this.take(true)}>${t('reminder.offer.button')}</button>
            <button class="btn small ghost end-offer-dismiss" @click=${() => this.take(false)}>${t('reminder.offer.dismiss')}</button></div></div>`
        : nothing}
    </div>`;
  }

  /** One compact line naming the missed words, at most `MISSED_SHOWN`, then "+N". */
  private renderMissed() {
    const words = this.missed;
    if (!words.length) return nothing;
    const shown = words.slice(0, MISSED_SHOWN), more = words.length - shown.length;
    const dot = t('common.dot');
    return html`<div class="end-again small">${t('end.again')} ${shown.map((w, n) => html`${n ? dot : ''}${word(w)}`)}${more
      ? html`${dot}${t('end.againMore', { n: more })}`
      : nothing}</div>`;
  }

  /** The headline. A first other than the fog's is the headline itself; the fog's heads its calendar line. */
  private renderHead(h: ReturnType<typeof headlineOf>) {
    if (!h) return nothing;
    if (h.kind === 'first' && h.first !== 'fog') {
      const [pre, post] = aroundWord(t(firstKey(h.first)), h.word);
      return html`<div class="end-head end-first end-first-${h.first}" data-kind=${h.first}>${pre}${word(h.word)}${post}</div>`;
    }
    if (h.kind === 'first') {
      const [pre, post] = aroundWord(t('end.head.fogFirst'), h.word);
      return html`<div class="end-head">${pre}${word(h.word)}${post}</div>`;
    }
    const text = h.kind === 'fog' ? t(h.n === 1 ? 'end.head.fogOne' : 'end.head.fog', { n: h.n })
      : h.kind === 'climbed' ? t(h.n === 1 ? 'end.head.climbedOne' : 'end.head.climbed', { n: h.n })
        : h.kind === 'repaired' ? t(h.n === 1 ? 'end.head.repairedOne' : 'end.head.repaired', { n: h.n })
        : h.kind === 'lamps' ? t(h.n === 1 ? 'end.head.lampsOne' : 'end.head.lamps', { n: h.n })
          : h.kind === 'ivy' ? t(h.n === 1 ? 'end.head.ivyOne' : 'end.head.ivy', { n: h.n })
            : t('end.head.none');
    return html`<div class="end-head ${h.kind === 'none' ? 'end-head-none' : ''}">${text}</div>`;
  }

  /** A first in one short line (the first word out of the fog: its calendar). */
  private renderFirst(kind: string, w: string) {
    const [pre, post] = aroundWord(t(firstKey(kind as Parameters<typeof firstKey>[0])), w);
    return html`<div class="end-first end-first-${kind} small" data-kind=${kind}>${pre}${word(w)}${post}</div>`;
  }

  /** The promise with the town's name (v1 ticket 40) in its own isolate, any script shaping right. */
  private named(line: string) {
    const [pre, post] = line.split('{town}');
    return post === undefined ? line : html`${pre}<bdi class="end-town" dir="auto">${this.end?.town}</bdi>${post}`;
  }

  /** One line and a button that opens the moment with its new lines marked. */
  private renderComeback() {
    const c = this.end?.comeback;
    if (!c) return nothing;
    const [pre, post = ''] = comebackLine(c, t, currentLang()).split('{title}');
    return html`<div class="end-comeback small" data-moment=${c.momentId}>
      ${pre}<bdi dir="auto"><em>${c.title}</em></bdi>${post}
      <button class="btn small end-comeback-open" style="margin-top:6px;display:block" @click=${() => go(`/moment/${c.momentId}#new=${c.new.join(',')}`)}>${t('week.comebackOpen')}</button>
    </div>`;
  }
}

/** The missed words named before "+N". */
const MISSED_SHOWN = 5;

/** A target word inline, at the text's own size, in its own direction. */
const word = (w: string) => html`<bdi class="ca" dir="auto">${w}</bdi>`;

declare global {
  interface HTMLElementTagNameMap { 'end-note': EndNote }
}
